import React from "react";
import { createContext, ReducerAction, useContext, useReducer } from "react";

type ErrorHandlerStateType = {
  showError: boolean;
  errorMessage: string;
};

const INITIAL_STATE: ErrorHandlerStateType = {
  showError: false,
  errorMessage: "",
};

const INITIAL_STATE_STORE = {
  errorMessage: "",
  toShowError: false,
  displayError: (param: string) => {},
  cancelErrorDialog: () => {},
};

const ErrorContext = createContext(INITIAL_STATE_STORE);

export const useErrorHandler = () => {
  const context = useContext(ErrorContext);
  if (context === undefined)
    throw new Error("useError must be used within ErrorHandlerProvider");
  return context;
};

export const ErrorHandlerProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const [state, dispatch] = useReducer(errorHandlerReducer, INITIAL_STATE);

  const showError = (error: string) => {
    dispatch({ type: ErrorHandlerActionEnum.SHOW_ERROR, payload: error });
  };
  const cancelErrorDialog = () => {
    dispatch({ type: ErrorHandlerActionEnum.CANCEL_ERROR, payload: "" });
  };

  return (
    <ErrorContext.Provider
      value={{
        displayError: showError,
        toShowError: state.showError,
        errorMessage: state.errorMessage,
        cancelErrorDialog,
      }}
    >
      {children}
    </ErrorContext.Provider>
  );
};

const errorHandlerReducer = (
  state = INITIAL_STATE,
  action: ErrorHandlerAction
): ErrorHandlerStateType => {
  const { type, payload } = action;

  switch (type) {
    case ErrorHandlerActionEnum.SHOW_ERROR:
      return {
        ...state,
        showError: true,
        errorMessage: payload,
      };
    case ErrorHandlerActionEnum.CANCEL_ERROR:
      return {
        ...state,
        showError: false,
      };
    default:
      return state;
  }
};

enum ErrorHandlerActionEnum {
  SHOW_ERROR = "SHOW_ERROR",
  CANCEL_ERROR = "CANCEL_ERROR",
}

interface ErrorHandlerAction {
  type: ErrorHandlerActionEnum;
  payload: string;
}
